import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Download,
  ArrowsClockwise,
  Funnel,
  CaretUp,
  CaretDown,
  Package,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getStockBadge = (stock, threshold = 10) => {
  if (stock === 0) {
    return <span className="stock-badge stock-badge-danger">Нет</span>;
  } else if (stock <= threshold) {
    return <span className="stock-badge stock-badge-warning">{stock}</span>;
  }
  return <span className="stock-badge stock-badge-success">{stock}</span>;
};

// Sort icon component
const SortIcon = ({ sortConfig, columnKey }) => {
  if (sortConfig.key !== columnKey) {
    return <span className="ml-1 text-zinc-300 text-xs">⇅</span>;
  }
  return sortConfig.direction === "asc" ? (
    <CaretUp size={14} className="ml-1 text-[#005BFF]" weight="bold" />
  ) : (
    <CaretDown size={14} className="ml-1 text-[#005BFF]" weight="bold" />
  );
};

// Sortable header component
const SortableHeader = ({ columnKey, children, className = "", align = "center", sortConfig, onSort }) => (
  <TableHead
    className={`cursor-pointer hover:bg-zinc-100 transition-colors select-none ${className}`}
    onClick={() => onSort(columnKey)}
  >
    <div className={`flex items-center ${align === "left" ? "justify-start" : "justify-center"}`}>
      {children}
      <SortIcon sortConfig={sortConfig} columnKey={columnKey} />
    </div>
  </TableHead>
);

export default function Stocks({ cachedData, cachedLoading, onRefetch }) {
  const [stocks, setStocks] = useState([]);
  const [turnoverData, setTurnoverData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [settings, setSettings] = useState({ low_stock_threshold: 10 });
  const [syncStatus, setSyncStatus] = useState(null);
  const [limit, setLimit] = useState(15);
  const [offset, setOffset] = useState(0);
  const [turnoverPeriod, setTurnoverPeriod] = useState(90); // 30, 60, 90 days

  // === REACT QUERY CACHE INTEGRATION (добавлено для кэширования) ===
  useEffect(() => {
    if (cachedData && !cachedLoading) {
      setStocks(cachedData.stocks || []);
      setSettings(cachedData.settings || { low_stock_threshold: 10 });
      setSyncStatus(cachedData.syncStatus);
      // Build turnover lookup
      const turnover = {};
      (cachedData.turnoverData || []).forEach(item => {
        turnover[item.sku] = item;
      });
      setTurnoverData(turnover);
      setLoading(false);
    }
  }, [cachedData, cachedLoading]);
  // === END CACHE INTEGRATION ===

  const fetchStocks = async (period = turnoverPeriod) => {
    try {
      const [stocksResponse, settingsResponse, turnoverResponse, statusResponse] = await Promise.all([
        api.get(`/fast/stocks`),
        api.get(`/settings`),
        api.get(`/fast/turnover?period=${period}`),
        api.get(`/sync/status`),
      ]);
      
      setSyncStatus(statusResponse.data);
      
      if (stocksResponse.data.synced) {
        setStocks(stocksResponse.data.items || []);
      }
      
      setSettings(settingsResponse.data);
      
      // Create turnover lookup by SKU
      const turnover = {};
      (turnoverResponse.data.items || []).forEach(item => {
        turnover[item.sku] = item;
      });
      setTurnoverData(turnover);
    } catch (error) {
      console.error("Error fetching stocks:", error);
      toast.error("Ошибка загрузки остатков");
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setTurnoverPeriod(Number(newPeriod));
    fetchStocks(Number(newPeriod));
  };

  const refreshStocks = async () => {
    setRefreshing(true);
    // Use cached refetch if available, otherwise use local fetch
    if (onRefetch) {
      await onRefetch();
    } else {
      await fetchStocks();
    }
    setRefreshing(false);
    toast.success("Данные обновлены");
  };

  const exportToCSV = async () => {
    try {
      const response = await api.get(`/export/stocks`, {
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `stocks_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Файл экспортирован");
    } catch (error) {
      console.error("Error exporting:", error);
      toast.error("Ошибка экспорта");
    }
  };

  useEffect(() => {
    // Skip if using cached data from wrapper
    if (cachedData) return;
    fetchStocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (key) => {
    let direction = "desc";
    if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = "asc";
    }
    setSortConfig({ key, direction });
  };

  const { displayedStocks, totalFiltered } = useMemo(() => {
    let result = [...stocks];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.product_name?.toLowerCase().includes(query) ||
          item.offer_id?.toLowerCase().includes(query) ||
          String(item.sku).includes(query)
      );
    }

    // Apply stock status filter
    const threshold = settings.low_stock_threshold || 10;
    if (stockFilter === "out") {
      result = result.filter((item) => item.total_stock === 0);
    } else if (stockFilter === "low") {
      result = result.filter(
        (item) => item.total_stock > 0 && item.total_stock <= threshold
      );
    } else if (stockFilter === "in_stock") {
      result = result.filter((item) => item.total_stock > threshold);
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        // Handle special keys for turnover data
        if (sortConfig.key === "daily_turnover") {
          const aTurnover = turnoverData[String(a.sku)] || {};
          const bTurnover = turnoverData[String(b.sku)] || {};
          aVal = aTurnover.daily_turnover || 0;
          bVal = bTurnover.daily_turnover || 0;
        } else if (sortConfig.key === "days_remaining") {
          const aTurnover = turnoverData[String(a.sku)] || {};
          const bTurnover = turnoverData[String(b.sku)] || {};
          const aDailyTurnover = aTurnover.daily_turnover || 0;
          const bDailyTurnover = bTurnover.daily_turnover || 0;
          aVal = aDailyTurnover > 0 ? Math.round(a.total_stock / aDailyTurnover) : 99999;
          bVal = bDailyTurnover > 0 ? Math.round(b.total_stock / bDailyTurnover) : 99999;
        } else if (sortConfig.key === "product_name") {
          // Handle product_name sorting - data comes in "name" field
          aVal = a.name || a.product_name || "";
          bVal = b.name || b.product_name || "";
        } else {
          aVal = a[sortConfig.key] || 0;
          bVal = b[sortConfig.key] || 0;
        }
        
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      });
    }

    // Save total before applying pagination
    const totalFiltered = result.length;
    
    // Apply pagination (offset and limit)
    const displayed = result.slice(offset, offset + limit);

    return { displayedStocks: displayed, totalFiltered };
  }, [stocks, searchQuery, stockFilter, sortConfig, settings.low_stock_threshold, turnoverData, limit, offset]);
  
  // Pagination calculations
  const totalPages = Math.ceil(totalFiltered / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-6" data-testid="stocks-page">
      {/* Header and actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <h3 className="font-chivo font-bold text-2xl text-zinc-900">
          Остатки товаров
        </h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportToCSV}
            data-testid="export-csv-btn"
          >
            <Download size={18} className="mr-2" />
            Экспорт CSV
          </Button>
          <Button
            onClick={refreshStocks}
            disabled={refreshing}
            className="btn-ozon"
            data-testid="refresh-stocks-btn"
          >
            <ArrowsClockwise
              size={18}
              className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Обновить
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="filters-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <Input
                placeholder="Поиск по названию, артикулу или SKU..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setOffset(0); // Reset pagination on search
                }}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={stockFilter} onValueChange={(v) => {
                setStockFilter(v);
                setOffset(0); // Reset pagination on filter change
              }}>
                <SelectTrigger className="w-[180px]" data-testid="stock-filter-select">
                  <Funnel size={16} className="mr-2" />
                  <SelectValue placeholder="Все товары" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все товары</SelectItem>
                  <SelectItem value="in_stock">В наличии</SelectItem>
                  <SelectItem value="low">Мало на складе</SelectItem>
                  <SelectItem value="out">Нет в наличии</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={String(turnoverPeriod)} onValueChange={handlePeriodChange}>
                <SelectTrigger className="w-[180px]" data-testid="turnover-period-select">
                  <SelectValue placeholder="Период оборачиваемости" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">Оборачиваемость 30 дн.</SelectItem>
                  <SelectItem value="60">Оборачиваемость 60 дн.</SelectItem>
                  <SelectItem value="90">Оборачиваемость 90 дн.</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card data-testid="stocks-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : displayedStocks.length > 0 ? (
            <div className="overflow-x-auto">
              <Table data-testid="stocks-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Артикул</TableHead>
                    <SortableHeader columnKey="product_name" align="left" className="min-w-[280px]" sortConfig={sortConfig} onSort={handleSort}>Товар</SortableHeader>
                    <SortableHeader columnKey="fbo_present" sortConfig={sortConfig} onSort={handleSort}>FBO</SortableHeader>
                    <SortableHeader columnKey="fbo_reserved" sortConfig={sortConfig} onSort={handleSort}>Резерв</SortableHeader>
                    <SortableHeader columnKey="fbs_present" sortConfig={sortConfig} onSort={handleSort}>FBS</SortableHeader>
                    <SortableHeader columnKey="total_stock" sortConfig={sortConfig} onSort={handleSort}>Всего</SortableHeader>
                    <SortableHeader columnKey="daily_turnover" sortConfig={sortConfig} onSort={handleSort}>Оборот/день</SortableHeader>
                    <SortableHeader columnKey="days_remaining" sortConfig={sortConfig} onSort={handleSort}>Хватит на</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedStocks.map((item, index) => {
                    // Get turnover data by SKU
                    const turnover = turnoverData[String(item.sku)] || {};
                    const dailyTurnover = turnover.daily_turnover || 0;
                    // Calculate days of stock remaining
                    const daysRemaining = dailyTurnover > 0 
                      ? Math.round(item.total_stock / dailyTurnover) 
                      : null;
                    
                    return (
                    <motion.tr
                      key={item.product_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.01, 0.5) }}
                      className="table-row-hover group"
                      data-testid={`stock-row-${item.product_id}`}
                    >
                      <TableCell className="px-2 py-3">
                        <div className="flex flex-col">
                          <span className="font-mono text-xs font-medium text-zinc-700">
                            {item.offer_id}
                          </span>
                          <span className="font-mono text-[10px] text-zinc-400">
                            SKU: {item.sku}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-2 py-3">
                        <div className="flex items-start gap-3">
                          {item.primary_image ? (
                            <img
                              src={item.primary_image}
                              alt={item.name || item.product_name}
                              className="w-12 h-12 object-cover rounded border border-zinc-200 flex-shrink-0"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-12 h-12 bg-zinc-100 rounded flex items-center justify-center flex-shrink-0 ${item.primary_image ? 'hidden' : ''}`}
                          >
                            <Package size={20} className="text-zinc-400" />
                          </div>
                          <div className="min-w-[200px] max-w-[300px]">
                            <div className="text-sm font-medium text-zinc-900 leading-tight line-clamp-3" title={item.name || item.product_name}>
                              {item.name || item.product_name}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <span className="font-mono font-medium">
                            {item.fbo_present}
                          </span>
                          {item.fbo_promised > 0 && (
                            <div className="text-xs text-blue-500">
                              +{item.fbo_promised} в пути
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <span className={`font-mono font-medium ${item.fbo_reserved > 0 ? 'text-amber-600' : 'text-zinc-400'}`}>
                            {item.fbo_reserved || 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-center">
                          <span className="font-mono font-medium">
                            {item.fbs_present}
                          </span>
                          {item.fbs_reserved > 0 && (
                            <span className="text-xs text-zinc-400 ml-1">
                              (+{item.fbs_reserved})
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-mono font-bold">
                        {item.total_stock}
                        {item.fbo_promised > 0 && (
                          <div className="text-xs text-blue-500 font-normal">
                            +{item.fbo_promised} в пути
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono font-medium text-[#005BFF]">
                          {dailyTurnover > 0 ? dailyTurnover.toFixed(2) : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {daysRemaining !== null ? (
                          <span className={`font-mono font-medium px-2 py-1 rounded ${
                            daysRemaining < 14 ? 'bg-red-100 text-red-700' : 
                            daysRemaining < 30 ? 'bg-amber-100 text-amber-700' : 
                            daysRemaining < 60 ? 'bg-blue-100 text-blue-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {daysRemaining} дн
                          </span>
                        ) : (
                          <span className="text-zinc-400">-</span>
                        )}
                      </TableCell>
                    </motion.tr>
                  )})}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="empty-state py-16">
              <Package size={48} className="text-zinc-300 mb-4" />
              <p className="text-lg font-medium text-zinc-600 mb-2">
                Товары не найдены
              </p>
              <p className="text-sm text-zinc-400">
                Попробуйте изменить параметры поиска или фильтры
              </p>
            </div>
          )}
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-zinc-200">
            <div className="text-sm text-zinc-600">
              Показано <span className="font-medium">{Math.min(offset + limit, totalFiltered)}</span> из <span className="font-medium">{totalFiltered}</span> товаров
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-600">Отображать:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setOffset(0);
                  }}
                  className="px-3 py-1.5 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={15}>15 товаров</option>
                  <option value={30}>30 товаров</option>
                  <option value={60}>60 товаров</option>
                  <option value={100}>100 товаров</option>
                </select>
              </div>
              
              {totalFiltered > limit && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                  >
                    Назад
                  </Button>
                  <span className="flex items-center px-3 text-sm text-zinc-500">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= totalFiltered}
                  >
                    Вперед
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
